import { Rng } from '../../core/rng';
import { stationSeed, type StationId, type Style } from './stations';
import { chordAtStep, fitPhrase, fitRange, generatePhrase, isChordTone, leadVoicing, PROGRESSIONS, resolveProgression, SCALES, transposePhrase, varyEnding, type BarSpec, type Chord, type Mode, type PhraseNote } from './theory';

/**
 * Original songs for the music stations, generated deterministically from `(station, song index)`.
 * A song is a key, a tempo and feel, an arrangement of sections (intro / verse / chorus / bridge /
 * solo / outro), a chord progression per section with nearest-voicing voice leading through the
 * whole song, a chorus hook (stated, varied, restated, resolved), a verse melody, a bridge line and
 * a solo. `songBar(song, bar)` renders one bar of it into note events (sixteenth steps) with the
 * arrangement's layering, drum fills, crashes, stabs and the fade or cold ending.
 */

export type Inst =
  | 'rhodes'
  | 'hammond'
  | 'hammondLead'
  | 'guitar'
  | 'guitarMute'
  | 'piano'
  | 'strings'
  | 'brass'
  | 'vibes'
  | 'melodica'
  | 'flute'
  | 'bass'
  | 'kick'
  | 'snare'
  | 'rim'
  | 'hat'
  | 'hatOpen'
  | 'ride'
  | 'crash'
  | 'tomLo'
  | 'tomHi'
  | 'brush'
  | 'tamb'
  | 'shaker'
  | 'conga';

export const PITCHED: readonly Inst[] = ['rhodes', 'hammond', 'hammondLead', 'guitar', 'guitarMute', 'piano', 'strings', 'brass', 'vibes', 'melodica', 'flute', 'bass'];

/** The event instrument a section's melody is written for (the organ lead gets its solo registration). */
export function melodyEventInst(inst: HookInst): Inst {
  return inst === 'hammond' ? 'hammondLead' : inst;
}

export interface NoteEvent {
  inst: Inst;
  /** Sixteenth-note step within the bar, 0..15. */
  step: number;
  /** MIDI note (0 for drums). */
  midi: number;
  /** Length in steps. */
  dur: number;
  vel: number;
}

export type SectionKind = 'intro' | 'verse' | 'chorus' | 'bridge' | 'solo' | 'outro';

export type Feel = 'motown' | 'ballad' | 'uptempo' | 'beat' | 'rnb' | 'mod' | 'bossa' | 'swing' | 'lounge' | 'ska' | 'rocksteady' | 'reggae';

export type HookInst = 'vibes' | 'brass' | 'guitar' | 'hammond' | 'melodica' | 'flute' | 'strings' | 'rhodes';
export type CompInst = 'rhodes' | 'hammond' | 'piano' | 'guitar';
export type BassVoice = 'soft' | 'pick' | 'finger' | 'upright';

export interface Section {
  kind: SectionKind;
  /** First bar (song-relative). */
  start: number;
  bars: number;
  /** Harmony per bar (1-2 chords). */
  chords: Chord[][];
  /** Comp voicing per bar per chord (voice-led through the song). */
  voicings: number[][][];
  /** Melody over the section (steps relative to the section start), or none. */
  melody: PhraseNote[];
  /** Which instrument carries the melody in this section. */
  melodyInst: HookInst | null;
  /** Semitones this section is transposed from the song key (the final chorus may lift). */
  shift: number;
  /** The n-th section of its kind (0-based): later verses/choruses are busier. */
  nth: number;
}

export interface BassRiffNote {
  step: number;
  /** Semitones above the chord root (12 = the octave, -1 = approach). */
  iv: number;
  dur: number;
}

export interface Song {
  station: StationId;
  index: number;
  title: string;
  artist: string;
  style: Style;
  feel: Feel;
  /** Tonic pitch class (0 = C). */
  tonic: number;
  mode: Mode;
  bpm: number;
  swing: number;
  sections: Section[];
  bars: number;
  hookInst: HookInst;
  compInst: CompInst;
  bassVoice: BassVoice;
  /** A per-song one-bar bass riff (reggae / mod); empty = style default lines. */
  bassRiff: BassRiffNote[];
  ending: 'fade' | 'cold';
  /** Strings / brass pads in the chorus. */
  pad: 'strings' | 'hammond' | null;
  seed: number;
}

// ---------------------------------------------------------------------------------------------
// Titles

const WORDS: Record<Style, { a: readonly string[]; n: readonly string[]; p: readonly string[]; artists: readonly string[] }> = {
  soul: {
    a: ['Midnight', 'Tender', 'Sunday', 'Back Street', 'Saturday', 'Borrowed', 'Velvet', 'Paper', 'Sweet', 'Last'],
    n: ['Tram', 'Kettle', 'Letter', 'Promise', 'Heart', 'Rain', 'Lamplight', 'Window', 'Ticket', 'Telephone'],
    p: ['Marlow', 'the Arches', 'Pilcher Street', 'the Basin', 'Mercy Square', 'the Parade', 'Kessmouth'],
    artists: ['The Marlow Set', 'Dora Pennant', 'Little Vic Calloway', 'The Gaslights', 'Ruby Tench & the Tides', 'The Velvet Lamps', 'Sonny Okoro'],
  },
  beat: {
    a: ['Chrome', 'Electric', 'Heavy', 'Clean', 'Loud', 'Sharp', 'Mohair', 'Downtown', 'Carnaby', 'Backfire'],
    n: ['Scooter', 'Bumper', 'Traffic', 'Shuffle', 'Weekender', 'Stomp', 'Hustle', 'Ticket', 'Motorway', 'Wardrobe'],
    p: ['the Parade', 'Pennant Road', 'the Ring Road', 'Tanner’s Row', 'the Viaduct', 'the Arcade'],
    artists: ['The Pennants', 'The Sharp End', 'Alfie Crane & the Fives', 'The Weekenders', 'The Mod Squadron', 'Benny Loach', 'The Ring Roads'],
  },
  easy: {
    a: ['Moonlit', 'Blue', 'Summer', 'Evening', 'Gentle', 'Silver', 'Golden', 'Autumn', 'Quiet', 'Lazy'],
    n: ['Waltz', 'Serenade', 'Lullaby', 'Theme', 'Breeze', 'Reverie', 'Holiday', 'Promenade', 'Sunset', 'Carousel'],
    p: ['Kessmouth', 'the Esplanade', 'Mercy Square', 'the Gardens', 'Brannoc Hill', 'the Pier'],
    artists: ['The Kessmouth Light Orchestra', 'Norman Pike & his Strings', 'The Esplanade Ensemble', 'Geoff Halloran Quartet', 'Cynthia Marsh', 'The Brannoc Singers'],
  },
  ska: {
    a: ['Rude', 'Basin', 'Sixpence', 'Quayside', 'Gaslight', 'Dockside', 'Rocksteady', 'Skinhead', 'Long', 'Tidal'],
    n: ['Skank', 'Shuffle', 'Rocksteady', 'Train', 'Moonstomp', 'Boat', 'Reggae', 'Rudie', 'Warehouse', 'Harbour'],
    p: ['the Quay', 'the Basin', 'Gullet Street', 'Bonded Alley', 'the Crane Yard', 'Kessmouth'],
    artists: ['The Basin Rockers', 'Delroy Quayle', 'The Gullet All Stars', 'Winston & the Longshore', 'Sister Pearl', 'The Bonded Alley Band', 'Prince Tench'],
  },
};

function titleFor(style: Style, rng: Rng): { title: string; artist: string } {
  const w = WORDS[style];
  const form = rng.int(0, 4);
  const a = rng.pick(w.a);
  const n = rng.pick(w.n);
  const p = rng.pick(w.p);
  const title = form === 0 ? `${a} ${n}` : form === 1 ? `${n} on ${p}` : form === 2 ? `${a} ${n} (${p})` : form === 3 ? `The ${n} of ${p}` : `${a} ${n} in ${p}`;
  return { title, artist: rng.pick(w.artists) };
}

// ---------------------------------------------------------------------------------------------
// Song generation

/** Onset templates (one bar, sixteenth steps) for hooks, by style. */
const HOOK_RHYTHMS: Record<Style, readonly (readonly number[])[]> = {
  soul: [
    [0, 3, 6, 8, 11, 14],
    [0, 2, 4, 6, 8, 12],
    [2, 4, 6, 10, 12, 14],
    [0, 4, 7, 10, 12],
    [0, 3, 4, 8, 11, 12, 14],
    [0, 6, 8, 12],
  ],
  beat: [
    [0, 2, 4, 6, 8, 10, 12, 14],
    [0, 3, 6, 8, 10, 12],
    [0, 2, 3, 6, 8, 11, 12, 14],
    [0, 4, 6, 8, 12, 14],
    [0, 2, 4, 8, 10, 12],
  ],
  easy: [
    [0, 4, 8, 12],
    [0, 6, 8, 12],
    [0, 2, 4, 8, 10],
    [0, 3, 8, 11],
    [4, 8, 12, 14],
    [0, 8, 10, 12],
  ],
  ska: [
    [0, 2, 4, 8, 10, 12],
    [2, 6, 10, 14],
    [0, 3, 6, 8, 11, 14],
    [0, 4, 6, 8, 12, 14],
    [0, 2, 6, 8, 10, 14],
  ],
};

/** Verse melodies sit lower and sparser. */
const VERSE_RHYTHMS: Record<Style, readonly (readonly number[])[]> = {
  soul: [
    [0, 4, 6, 8],
    [2, 4, 8, 12],
    [0, 3, 8, 10, 12],
    [0, 6, 12],
  ],
  beat: [
    [0, 4, 8, 10, 12],
    [0, 2, 4, 8],
    [0, 6, 8, 12, 14],
  ],
  easy: [
    [0, 8],
    [0, 6, 12],
    [4, 8, 12],
    [0, 8, 12],
  ],
  ska: [
    [0, 4, 8, 12],
    [2, 6, 8, 12],
    [0, 8, 10, 12],
  ],
};

const SOLO_RHYTHMS: Record<Style, readonly (readonly number[])[]> = {
  soul: [
    [0, 2, 3, 6, 8, 10, 12, 14],
    [0, 2, 4, 6, 8, 11, 12],
    [2, 4, 6, 8, 10, 12, 14],
  ],
  beat: [
    [0, 2, 4, 6, 8, 10, 12, 14],
    [0, 1, 2, 4, 6, 8, 10, 12, 14],
    [0, 3, 4, 6, 8, 10, 11, 12, 14],
  ],
  easy: [
    [0, 2, 4, 6, 8, 12],
    [0, 3, 6, 8, 10, 12, 14],
    [2, 4, 6, 8, 12],
  ],
  ska: [
    [0, 2, 4, 6, 8, 10, 12, 14],
    [2, 4, 6, 10, 12, 14],
    [0, 3, 6, 8, 11, 12, 14],
  ],
};

interface Plan {
  kind: SectionKind;
  bars: number;
}

function arrangement(style: Style, feel: Feel, rng: Rng): Plan[] {
  const plan: Plan[] = [];
  const introBars = feel === 'ballad' || feel === 'lounge' ? 4 : rng.chance(0.3) ? 2 : 4;
  plan.push({ kind: 'intro', bars: introBars });
  const verseBars = feel === 'rnb' ? 12 : 8;
  plan.push({ kind: 'verse', bars: verseBars });
  plan.push({ kind: 'chorus', bars: 8 });
  plan.push({ kind: 'verse', bars: verseBars });
  plan.push({ kind: 'chorus', bars: 8 });
  const r = rng.next();
  if (r < 0.4) plan.push({ kind: 'bridge', bars: rng.chance(0.5) ? 4 : 8 });
  else if (r < 0.8) plan.push({ kind: 'solo', bars: 8 });
  else {
    plan.push({ kind: 'bridge', bars: 4 });
    plan.push({ kind: 'solo', bars: 8 });
  }
  plan.push({ kind: 'chorus', bars: 8 });
  if (rng.chance(0.3)) plan.push({ kind: 'chorus', bars: 8 });
  plan.push({ kind: 'outro', bars: style === 'easy' ? 4 : rng.chance(0.5) ? 4 : 8 });
  return plan;
}

function pickFeel(style: Style, rng: Rng): { feel: Feel; bpm: number; swing: number } {
  switch (style) {
    case 'soul': {
      const feel = rng.weighted<Feel>(['motown', 'ballad', 'uptempo'], [5, 3, 3]);
      return feel === 'ballad' ? { feel, bpm: rng.int(66, 82), swing: rng.range(0.35, 0.6) } : feel === 'uptempo' ? { feel, bpm: rng.int(118, 134), swing: rng.range(0, 0.15) } : { feel, bpm: rng.int(96, 116), swing: rng.range(0.1, 0.35) };
    }
    case 'beat': {
      const feel = rng.weighted<Feel>(['beat', 'rnb', 'mod'], [4, 3, 4]);
      return feel === 'rnb' ? { feel, bpm: rng.int(112, 132), swing: rng.range(0.45, 0.62) } : { feel, bpm: rng.int(136, 164), swing: rng.range(0, 0.12) };
    }
    case 'easy': {
      const feel = rng.weighted<Feel>(['bossa', 'swing', 'lounge'], [4, 3, 3]);
      return feel === 'bossa' ? { feel, bpm: rng.int(108, 124), swing: 0 } : feel === 'swing' ? { feel, bpm: rng.int(104, 130), swing: rng.range(0.55, 0.68) } : { feel, bpm: rng.int(68, 84), swing: rng.range(0.25, 0.45) };
    }
    default: {
      const feel = rng.weighted<Feel>(['ska', 'rocksteady', 'reggae'], [4, 3, 3]);
      return feel === 'ska' ? { feel, bpm: rng.int(122, 138), swing: rng.range(0, 0.15) } : feel === 'rocksteady' ? { feel, bpm: rng.int(86, 100), swing: rng.range(0.1, 0.3) } : { feel, bpm: rng.int(72, 84), swing: rng.range(0.15, 0.35) };
    }
  }
}

function templates(style: Style, minor: boolean): readonly (readonly BarSpec[])[] {
  const key = `${style}${minor ? 'Minor' : 'Major'}`;
  return PROGRESSIONS[key] as readonly (readonly BarSpec[])[];
}

/** Melody scale for a key: the mode, made mixolydian when the harmony leans on bVII. */
function melodyScale(mode: Mode, chords: readonly (readonly Chord[])[]): readonly number[] {
  if (mode === 'major' && chords.some((b) => b.some((c) => c.st === 10))) return SCALES.mixolydian;
  if (mode === 'minor' && chords.some((b) => b.some((c) => c.st === 5 && (c.quality === 'maj' || c.quality === 'dom7')))) return SCALES.dorian;
  return SCALES[mode];
}

function bassRiffFor(feel: Feel, rng: Rng): BassRiffNote[] {
  if (feel === 'reggae') {
    const riffs: readonly (readonly BassRiffNote[])[] = [
      [{ step: 0, iv: 0, dur: 3 }, { step: 3, iv: 0, dur: 1 }, { step: 6, iv: 7, dur: 2 }, { step: 10, iv: 10, dur: 1 }, { step: 12, iv: 12, dur: 2 }],
      [{ step: 0, iv: 0, dur: 2 }, { step: 4, iv: 0, dur: 1 }, { step: 6, iv: 5, dur: 2 }, { step: 8, iv: 7, dur: 2 }, { step: 14, iv: 12, dur: 1 }],
      [{ step: 0, iv: 0, dur: 5 }, { step: 6, iv: 7, dur: 2 }, { step: 8, iv: 0, dur: 2 }, { step: 11, iv: 3, dur: 1 }, { step: 12, iv: 7, dur: 2 }],
      [{ step: 0, iv: 0, dur: 2 }, { step: 3, iv: 12, dur: 1 }, { step: 6, iv: 0, dur: 2 }, { step: 8, iv: 7, dur: 2 }, { step: 12, iv: 10, dur: 1 }, { step: 14, iv: 7, dur: 1 }],
    ];
    return rng.pick(riffs).map((n) => ({ ...n }));
  }
  if (feel === 'mod' || feel === 'beat') {
    const riffs: readonly (readonly BassRiffNote[])[] = [
      [{ step: 0, iv: 0, dur: 2 }, { step: 2, iv: 0, dur: 2 }, { step: 4, iv: 0, dur: 2 }, { step: 6, iv: 12, dur: 2 }, { step: 8, iv: 0, dur: 2 }, { step: 10, iv: 0, dur: 2 }, { step: 12, iv: 7, dur: 2 }, { step: 14, iv: 10, dur: 2 }],
      [{ step: 0, iv: 0, dur: 3 }, { step: 3, iv: 0, dur: 1 }, { step: 4, iv: 7, dur: 2 }, { step: 6, iv: 0, dur: 2 }, { step: 8, iv: 0, dur: 2 }, { step: 10, iv: 10, dur: 2 }, { step: 12, iv: 12, dur: 2 }, { step: 14, iv: 7, dur: 2 }],
      [{ step: 0, iv: 0, dur: 2 }, { step: 2, iv: 0, dur: 2 }, { step: 4, iv: 3, dur: 2 }, { step: 6, iv: 5, dur: 2 }, { step: 8, iv: 7, dur: 2 }, { step: 10, iv: 7, dur: 2 }, { step: 12, iv: 5, dur: 2 }, { step: 14, iv: 3, dur: 2 }],
    ];
    return rng.pick(riffs).map((n) => ({ ...n }));
  }
  return [];
}

/** Build a song. Pure and deterministic in `(station, index)`. */
export function generateSong(station: StationId, index: number): Song {
  const style = (station === 'talk' ? 'easy' : station) as Style;
  const seed = stationSeed(station, 11, index);
  const rng = new Rng(seed);
  const { feel, bpm, swing } = pickFeel(style, rng);
  const minor = rng.chance(style === 'easy' ? 0.3 : style === 'ska' ? 0.25 : 0.3);
  const mode: Mode = minor ? 'minor' : 'major';
  // Home keys of the period's bands: guitar / horn friendly.
  const tonic = rng.pick(minor ? [9, 2, 4, 7, 0, 5] : [0, 7, 2, 5, 10, 9, 3, 4]);
  const { title, artist } = titleFor(style, rng);
  const plan = arrangement(style, feel, rng);
  const tpl = templates(style, minor);
  const verseT = rng.pick(tpl);
  let chorusT = rng.pick(tpl);
  if (chorusT === verseT && tpl.length > 1) chorusT = tpl[(tpl.indexOf(verseT) + 1 + rng.int(0, tpl.length - 2)) % tpl.length] as readonly BarSpec[];
  const bridgeT = rng.pick(PROGRESSIONS[minor ? 'bridgeMinor' : 'bridgeMajor'] as readonly (readonly BarSpec[])[]);
  const lift = style !== 'ska' && rng.chance(0.3) ? rng.pick([1, 2]) : 0;
  const hookInst: HookInst =
    style === 'soul' ? rng.weighted<HookInst>(['vibes', 'brass', 'hammond', 'strings', 'rhodes'], [3, 3, 2, feel === 'ballad' ? 3 : 1, 1])
    : style === 'beat' ? rng.weighted<HookInst>(['guitar', 'hammond', 'brass'], [4, 3, 2])
    : style === 'easy' ? rng.weighted<HookInst>(['flute', 'vibes', 'strings', 'rhodes'], [3, 3, 3, 1])
    : rng.weighted<HookInst>(['melodica', 'brass', 'hammond', 'guitar'], [3, 3, 2, 1]);
  const compInst: CompInst = style === 'soul' ? (rng.chance(0.75) ? 'rhodes' : 'hammond') : style === 'beat' ? (rng.chance(0.6) ? 'hammond' : 'guitar') : style === 'easy' ? (rng.chance(0.6) ? 'rhodes' : 'piano') : rng.chance(0.6) ? 'piano' : 'guitar';
  const bassVoice: BassVoice = style === 'soul' ? 'finger' : style === 'beat' ? 'pick' : style === 'easy' ? 'upright' : 'soft';
  const pad: Song['pad'] = style === 'easy' || (style === 'soul' && feel === 'ballad') ? 'strings' : style === 'soul' && rng.chance(0.5) ? 'hammond' : style === 'beat' && compInst === 'guitar' && rng.chance(0.6) ? 'hammond' : null;
  const ending: Song['ending'] = style === 'easy' ? (rng.chance(0.7) ? 'fade' : 'cold') : rng.chance(0.55) ? 'fade' : 'cold';
  const bassRiff = bassRiffFor(feel, rng);

  // Sections with harmony.
  const sections: Section[] = [];
  let start = 0;
  const counts: Partial<Record<SectionKind, number>> = {};
  let chorusesLeft = plan.filter((p) => p.kind === 'chorus').length;
  for (const p of plan) {
    const nth = counts[p.kind] ?? 0;
    counts[p.kind] = nth + 1;
    let shift = 0;
    if (p.kind === 'chorus') {
      chorusesLeft--;
      if (chorusesLeft === 0 && lift) shift = lift;
    } else if (p.kind === 'outro' && lift) shift = lift;
    const t = p.kind === 'bridge' ? bridgeT : p.kind === 'verse' || p.kind === 'solo' ? verseT : chorusT;
    const chords = resolveProgression(t, tonic + shift, p.bars);
    sections.push({ kind: p.kind, start, bars: p.bars, chords, voicings: [], melody: [], melodyInst: null, shift, nth });
    start += p.bars;
  }

  // Voice leading through the whole song.
  const compLo = compInst === 'guitar' ? 52 : compInst === 'piano' ? 55 : 53;
  const compHi = compLo + 24;
  let prev: number[] | null = null;
  for (const s of sections) {
    for (const bar of s.chords) {
      const vs: number[][] = [];
      for (const c of bar) {
        const v = leadVoicing(c, prev, compLo, compHi);
        vs.push(v);
        prev = v;
      }
      s.voicings.push(vs);
    }
  }

  // Melodies. The chorus hook is written once against the first chorus and reused (shifted) later.
  const firstChorus = sections.find((s) => s.kind === 'chorus') as Section;
  const scale = melodyScale(mode, firstChorus.chords);
  const hookLo = style === 'easy' ? 67 : style === 'beat' ? 60 : 62;
  const hookHi = hookLo + 17;
  const hookRng = new Rng(seed ^ 0x1234567);
  const hookA = generatePhrase({ rng: hookRng, scale, tonic, chords: firstChorus.chords.slice(0, 2), rhythms: HOOK_RHYTHMS[style], lo: hookLo, hi: hookHi, leap: style === 'beat' ? 0.3 : 0.2, stacc: style === 'easy' ? 0.15 : 0.35, cadence: false });
  const hookB = varyEnding(hookA, hookRng, firstChorus.chords.slice(2, 4), tonic, true);
  const hookEnd = generatePhrase({ rng: new Rng(seed ^ 0x7654321), scale, tonic, chords: firstChorus.chords.slice(6, 8), rhythms: [HOOK_RHYTHMS[style][0] as readonly number[]], lo: hookLo, hi: hookHi, from: hookA[0]?.midi, cadence: true });
  // The hook is stated (A), varied (B), restated (A) and resolved (End); where a slot's chords differ
  // from the ones the motif was written against (8- and 12-bar progressions), the motif is re-fitted
  // to them, as a songwriter answers a phrase over the next change.
  const chorusMelody = (bars: number, chords: readonly (readonly Chord[])[]): PhraseNote[] => {
    const out: PhraseNote[] = [];
    for (let b = 0; b < bars; b += 2) {
      const last = b + 2 >= bars;
      const src = last ? hookEnd : (b / 2) % 2 === 0 ? hookA : hookB;
      for (const n of fitPhrase(src, chords.slice(b, b + 2), scale, tonic, hookLo, hookHi, last)) out.push({ ...n, step: n.step + b * 16 });
    }
    return out;
  };
  const verseRng = new Rng(seed ^ 0x2468ace);
  const firstVerse = sections.find((s) => s.kind === 'verse') as Section;
  const vScale = melodyScale(mode, firstVerse.chords);
  const verseA = generatePhrase({ rng: verseRng, scale: vScale, tonic, chords: firstVerse.chords.slice(0, 2), rhythms: VERSE_RHYTHMS[style], lo: hookLo - 7, hi: hookHi - 9, leap: 0.1, stacc: 0.2, cadence: false });
  const verseB = varyEnding(verseA, verseRng, firstVerse.chords.slice(2, 4), tonic, false);
  const verseMelody = (bars: number): PhraseNote[] => {
    const out: PhraseNote[] = [];
    for (let b = 0; b < bars; b += 2) {
      const src = (b / 2) % 2 === 0 ? verseA : verseB;
      for (const n of fitPhrase(src, firstVerse.chords.slice(b, b + 2), vScale, tonic, hookLo - 7, hookHi - 9)) out.push({ ...n, step: n.step + b * 16 });
    }
    return out;
  };
  for (const s of sections) {
    if (s.kind === 'chorus' || s.kind === 'outro' || s.kind === 'intro') {
      const bars = s.kind === 'intro' ? Math.min(s.bars, 4) : s.bars;
      // Melodies are written in the home key and transposed with the section (the final-chorus lift).
      s.melody = transposePhrase(chorusMelody(bars, s.kind === 'intro' ? s.chords : firstChorus.chords), s.shift, hookLo, hookHi + 2);
      s.melodyInst = s.kind === 'intro' ? (hookInst === 'strings' || hookInst === 'flute' ? 'vibes' : hookInst) : hookInst;
    } else if (s.kind === 'verse') {
      // Second verse: the melody is carried quietly (a counter line); first verse mostly rhythm section.
      s.melody = verseMelody(s.bars);
      s.melodyInst = style === 'easy' ? 'rhodes' : style === 'ska' ? 'hammond' : hookInst === 'brass' ? 'vibes' : hookInst;
    } else if (s.kind === 'bridge') {
      const bScale = melodyScale(mode, s.chords);
      s.melody = generatePhrase({ rng: new Rng(seed ^ 0x3579bdf), scale: bScale, tonic, chords: s.chords, rhythms: VERSE_RHYTHMS[style], lo: hookLo + 2, hi: hookHi + 4, leap: 0.25, stacc: 0.1, cadence: false });
      s.melodyInst = pad === 'strings' ? 'strings' : hookInst === 'brass' ? 'hammond' : hookInst;
    } else if (s.kind === 'solo') {
      const sRng = new Rng(seed ^ 0x9bdf135);
      const sScale = melodyScale(mode, s.chords);
      const out: PhraseNote[] = [];
      let from: number | undefined;
      for (let b = 0; b < s.bars; b += 2) {
        const ph = generatePhrase({ rng: sRng, scale: sScale, tonic, chords: s.chords.slice(b, b + 2), rhythms: SOLO_RHYTHMS[style], lo: hookLo + 3, hi: hookHi + 7, from, leap: 0.35, stacc: 0.4, cadence: b + 2 >= s.bars });
        for (const n of ph) out.push({ ...n, step: n.step + b * 16 });
        from = ph[ph.length - 1]?.midi;
      }
      s.melody = out;
      s.melodyInst = style === 'soul' ? (compInst === 'hammond' ? 'vibes' : 'hammond') : style === 'beat' ? (hookInst === 'guitar' ? 'hammond' : 'guitar') : style === 'easy' ? 'vibes' : 'melodica';
    }
  }

  return { station, index, title, artist, style, feel, tonic, mode, bpm, swing, sections, bars: start, hookInst, compInst, bassVoice, bassRiff, ending, pad, seed };
}

/** Section containing song bar `bar` (clamped). */
export function sectionAt(song: Song, bar: number): Section {
  for (const s of song.sections) if (bar < s.start + s.bars) return s;
  return song.sections[song.sections.length - 1] as Section;
}

// ---------------------------------------------------------------------------------------------
// Bar rendering

function ev(inst: Inst, step: number, midi: number, dur: number, vel: number): NoteEvent {
  return { inst, step, midi, dur, vel: Math.max(0.05, Math.min(1, vel)) };
}

interface BarCtx {
  song: Song;
  sec: Section;
  /** Bar within the section. */
  b: number;
  rng: Rng;
  chords: readonly Chord[];
  next: Chord;
  /** Bass root of the first chord. */
  root: number;
  events: NoteEvent[];
  /** 0..1 overall intensity of the section. */
  energy: number;
  /** Last bar of a 4-bar group / of the section. */
  fill4: boolean;
  fill8: boolean;
  lastBar: boolean;
  firstBar: boolean;
}

function energyOf(sec: Section): number {
  switch (sec.kind) {
    case 'intro':
      return 0.55;
    case 'verse':
      return sec.nth === 0 ? 0.55 : 0.7;
    case 'chorus':
      return sec.nth === 0 ? 0.85 : 1;
    case 'bridge':
      return 0.6;
    case 'solo':
      return 0.8;
    default:
      return 0.9;
  }
}

/** Chord root as a bass note in [lo, hi]. */
function bassRoot(c: Chord, lo = 28, hi = 50): number {
  return fitRange(c.pc + 36, lo, hi);
}

function approach(from: number, target: number, rng: Rng, lo: number, hi: number): number {
  const t = fitRange(target, from - 7, from + 7);
  const opts = [t - 1, t + 1, t - 2, t + 7];
  return fitRange(rng.pick(opts), lo, hi);
}

// ---- drums ----------------------------------------------------------------------------------

function fillSnare(c: BarCtx, from: number, vel: number): void {
  for (let s = from; s < 16; s++) {
    if (s === 15 && c.rng.chance(0.4)) c.events.push(ev('tomLo', s, 0, 1, vel));
    else c.events.push(ev(s % 2 === 0 ? 'snare' : c.rng.chance(0.5) ? 'tomHi' : 'snare', s, 0, 1, vel * (s % 4 === 0 ? 1 : 0.75)));
  }
}

function drumsSoul(c: BarCtx): void {
  const { rng, events, song, energy } = c;
  const ballad = song.feel === 'ballad';
  const e = events;
  if (c.firstBar && c.sec.kind !== 'intro') e.push(ev('crash', 0, 0, 4, 0.7 * energy));
  e.push(ev('kick', 0, 0, 1, 0.95));
  if (!ballad && rng.chance(0.6)) e.push(ev('kick', 6, 0, 1, 0.6));
  e.push(ev('kick', 8, 0, 1, 0.85));
  if (!ballad && rng.chance(0.5)) e.push(ev('kick', 11, 0, 1, 0.55));
  if (c.sec.kind === 'intro' && c.b === 0) {
    // Count-in kind of intro: hats only for the first bar.
  }
  e.push(ev('snare', 4, 0, 1, 0.95));
  e.push(ev('snare', 12, 0, 1, 0.95));
  if (energy > 0.8 && rng.chance(0.35)) e.push(ev('snare', 15, 0, 1, 0.3));
  if (song.feel === 'uptempo' || energy > 0.9) e.push(ev('snare', 10, 0, 1, 0.25));
  const hatStep = ballad ? 2 : 2;
  for (let s = 0; s < 16; s += hatStep) e.push(ev('hat', s, 0, 1, s % 4 === 0 ? 0.45 : 0.3));
  if (rng.chance(0.2)) e.push(ev('hatOpen', 14, 0, 2, 0.35));
  if (energy >= 0.7) {
    // Tambourine: 2 and 4 with sixteenth jangle before the backbeat.
    e.push(ev('tamb', 4, 0, 1, 0.6), ev('tamb', 12, 0, 1, 0.6));
    if (energy >= 0.85) e.push(ev('tamb', 3, 0, 1, 0.25), ev('tamb', 11, 0, 1, 0.25), ev('tamb', 8, 0, 1, 0.3));
  }
  if (c.fill8 && !ballad) fillSnare(c, 12, 0.8);
  else if (c.fill4 && rng.chance(0.5)) e.push(ev('snare', 14, 0, 1, 0.55), ev('snare', 15, 0, 1, 0.65));
  if (c.lastBar && song.ending === 'cold' && c.sec.kind === 'outro') {
    c.events.length = 0;
    e.push(ev('kick', 0, 0, 1, 1), ev('crash', 0, 0, 8, 0.9));
  }
}

function drumsBeat(c: BarCtx): void {
  const { rng, events: e, song, energy } = c;
  if (c.firstBar && c.sec.kind !== 'intro') e.push(ev('crash', 0, 0, 4, 0.75));
  const rnb = song.feel === 'rnb';
  e.push(ev('kick', 0, 0, 1, 1));
  if (rng.chance(rnb ? 0.3 : 0.6)) e.push(ev('kick', 7, 0, 1, 0.7));
  e.push(ev('kick', 8, 0, 1, 0.85));
  if (rng.chance(0.4)) e.push(ev('kick', 14, 0, 1, 0.5));
  e.push(ev('snare', 4, 0, 1, 1), ev('snare', 12, 0, 1, 1));
  if (energy > 0.85 && rng.chance(0.3)) e.push(ev('snare', 11, 0, 1, 0.35));
  const ride = c.sec.kind === 'chorus' || c.sec.kind === 'solo';
  for (let s = 0; s < 16; s += 2) e.push(ev(ride ? 'ride' : 'hat', s, 0, 1, s % 4 === 0 ? 0.5 : 0.32));
  if (energy >= 0.85) e.push(ev('tamb', 4, 0, 1, 0.5), ev('tamb', 12, 0, 1, 0.5));
  if (c.fill8) fillSnare(c, rng.chance(0.5) ? 8 : 12, 0.85);
  else if (c.fill4 && rng.chance(0.6)) e.push(ev('tomHi', 13, 0, 1, 0.6), ev('tomLo', 14, 0, 1, 0.7), ev('snare', 15, 0, 1, 0.6));
  if (c.lastBar && song.ending === 'cold' && c.sec.kind === 'outro') {
    c.events.length = 0;
    e.push(ev('kick', 0, 0, 1, 1), ev('snare', 0, 0, 1, 1), ev('crash', 0, 0, 8, 1));
  }
}

function drumsEasy(c: BarCtx): void {
  const { rng, events: e, song, energy } = c;
  const bossa = song.feel === 'bossa';
  const swing = song.feel === 'swing';
  if (bossa) {
    e.push(ev('kick', 0, 0, 1, 0.6), ev('kick', 6, 0, 1, 0.45), ev('kick', 8, 0, 1, 0.55), ev('kick', 14, 0, 1, 0.45));
    // Bossa rim clave (3-2 or 2-3 by bar parity).
    const clave = c.b % 2 === 0 ? [0, 3, 6, 10, 12] : [2, 4, 8, 11, 14];
    for (const s of clave) e.push(ev('rim', s, 0, 1, 0.55));
    for (let s = 0; s < 16; s += 2) e.push(ev('hat', s, 0, 1, s % 4 === 0 ? 0.3 : 0.2));
    if (energy >= 0.7) for (let s = 0; s < 16; s += 2) e.push(ev('shaker', s, 0, 1, s % 4 === 2 ? 0.4 : 0.22));
    if (energy >= 0.7 && rng.chance(0.5)) e.push(ev('conga', 3, 0, 1, 0.4), ev('conga', 7, 0, 1, 0.3), ev('conga', 11, 0, 1, 0.45), ev('conga', 15, 0, 1, 0.3));
  } else if (swing) {
    e.push(ev('kick', 0, 0, 1, 0.45), ev('kick', 8, 0, 1, 0.4));
    for (let s = 0; s < 16; s += 4) e.push(ev('ride', s, 0, 1, 0.45), ev('ride', s + 3, 0, 1, 0.25));
    e.push(ev('hat', 4, 0, 1, 0.35), ev('hat', 12, 0, 1, 0.35));
    e.push(ev('brush', 4, 0, 4, 0.45), ev('brush', 12, 0, 4, 0.5));
    if (c.fill8) e.push(ev('snare', 12, 0, 1, 0.4), ev('snare', 13, 0, 1, 0.3), ev('snare', 14, 0, 1, 0.5), ev('snare', 15, 0, 1, 0.35));
  } else {
    // Lounge ballad: brushes swishing, a soft kick.
    e.push(ev('kick', 0, 0, 1, 0.5), ev('kick', 8, 0, 1, 0.4));
    e.push(ev('brush', 0, 0, 4, 0.3), ev('brush', 4, 0, 4, 0.5), ev('brush', 8, 0, 4, 0.3), ev('brush', 12, 0, 4, 0.55));
    for (let s = 2; s < 16; s += 4) if (rng.chance(0.6)) e.push(ev('hat', s, 0, 1, 0.2));
    if (energy >= 0.8) e.push(ev('shaker', 2, 0, 1, 0.25), ev('shaker', 6, 0, 1, 0.3), ev('shaker', 10, 0, 1, 0.25), ev('shaker', 14, 0, 1, 0.3));
  }
  if (c.firstBar && c.sec.kind === 'chorus') e.push(ev('crash', 0, 0, 6, 0.4));
  if (c.lastBar && song.ending === 'cold' && c.sec.kind === 'outro') {
    c.events.length = 0;
    e.push(ev('crash', 0, 0, 8, 0.5), ev('kick', 0, 0, 1, 0.6));
  }
}

function drumsSka(c: BarCtx): void {
  const { rng, events: e, song, energy } = c;
  const feel = song.feel;
  if (feel === 'ska') {
    e.push(ev('kick', 0, 0, 1, 0.95), ev('kick', 8, 0, 1, 0.85));
    if (rng.chance(0.3)) e.push(ev('kick', 6, 0, 1, 0.5));
    e.push(ev('rim', 4, 0, 1, 0.85), ev('rim', 12, 0, 1, 0.9));
    for (const s of [2, 6, 10, 14]) e.push(ev('hat', s, 0, 1, 0.5));
    for (const s of [0, 4, 8, 12]) e.push(ev('hat', s, 0, 1, 0.25));
    if (rng.chance(0.3)) e.push(ev('hatOpen', 14, 0, 2, 0.4));
  } else if (feel === 'rocksteady') {
    e.push(ev('kick', 0, 0, 1, 0.8), ev('kick', 8, 0, 1, 0.95), ev('kick', 11, 0, 1, 0.4));
    e.push(ev('rim', 8, 0, 1, 0.9));
    if (rng.chance(0.5)) e.push(ev('rim', 4, 0, 1, 0.4));
    for (let s = 0; s < 16; s += 2) e.push(ev('hat', s, 0, 1, s % 4 === 2 ? 0.5 : 0.3));
  } else {
    // One drop: kick and rim together on three, hats with the off-beat accent.
    e.push(ev('kick', 8, 0, 1, 1), ev('rim', 8, 0, 1, 0.9));
    if (rng.chance(0.4)) e.push(ev('kick', 0, 0, 1, 0.35));
    for (let s = 0; s < 16; s += 2) e.push(ev('hat', s, 0, 1, s % 4 === 2 ? 0.55 : 0.28));
    if (rng.chance(0.35)) e.push(ev('hatOpen', 6, 0, 2, 0.35));
    if (energy >= 0.8) e.push(ev('shaker', 2, 0, 1, 0.3), ev('shaker', 6, 0, 1, 0.35), ev('shaker', 10, 0, 1, 0.3), ev('shaker', 14, 0, 1, 0.35));
  }
  if (c.firstBar && c.sec.kind === 'chorus') e.push(ev('crash', 0, 0, 4, 0.55));
  if (c.fill8) {
    for (const s of [12, 13, 14, 15]) e.push(ev(s % 2 ? 'tomHi' : 'snare', s, 0, 1, 0.6 + 0.1 * (s - 12)));
  } else if (c.fill4 && rng.chance(0.4)) e.push(ev('snare', 14, 0, 1, 0.5), ev('snare', 15, 0, 1, 0.6));
  if (c.lastBar && song.ending === 'cold' && c.sec.kind === 'outro') {
    c.events.length = 0;
    e.push(ev('kick', 0, 0, 1, 1), ev('crash', 0, 0, 8, 0.8));
  }
}

// ---- bass -----------------------------------------------------------------------------------

function bassFromRiff(c: BarCtx, riff: readonly BassRiffNote[], vel: number): void {
  const chord = c.chords[0] as Chord;
  const split = c.chords.length > 1;
  for (const n of riff) {
    const ch = split && n.step >= 8 ? (c.chords[1] as Chord) : chord;
    const root = bassRoot(ch);
    let iv = n.iv;
    // Keep the third honest for the chord quality.
    if (iv === 3 || iv === 4) iv = ch.quality.startsWith('min') || ch.quality === 'dim7' || ch.quality === 'm7b5' ? 3 : 4;
    if (iv === 10 && (ch.quality === 'maj' || ch.quality === 'maj7' || ch.quality === 'maj6' || ch.quality === 'add9' || ch.quality === 'maj9')) iv = 9;
    const last = n.step >= 14;
    const midi = last && c.rng.chance(0.5) ? approach(root, bassRoot(c.next), c.rng, 28, 52) : fitRange(root + iv, 28, 52);
    c.events.push(ev('bass', n.step, midi, n.dur, vel * (n.step % 8 === 0 ? 1 : 0.8)));
  }
}

function bassSoul(c: BarCtx): void {
  const { rng, song } = c;
  const chord = c.chords[0] as Chord;
  const root = bassRoot(chord);
  const minor = chord.quality.startsWith('min');
  const third = minor ? 3 : 4;
  const nextRoot = bassRoot(c.next);
  const e = c.events;
  if (song.feel === 'ballad') {
    e.push(ev('bass', 0, root, 6, 0.9));
    if (c.chords.length > 1) e.push(ev('bass', 8, bassRoot(c.chords[1] as Chord), 6, 0.8));
    else e.push(ev('bass', 8, fitRange(root + (rng.chance(0.5) ? 7 : 12), 28, 50), 4, 0.7));
    e.push(ev('bass', 14, approach(root, nextRoot, rng, 28, 50), 2, 0.6));
    return;
  }
  // Motown eighths: root, octave, fifth, sixth / third, chromatic approach.
  const patterns: readonly (readonly [number, number][])[] = [
    [[0, 0], [2, 0], [4, 7], [6, 12], [8, 0], [10, 7], [12, 9], [14, -99]],
    [[0, 0], [3, 12], [6, 7], [8, 0], [10, third], [12, 7], [14, -99]],
    [[0, 0], [2, 12], [4, 0], [6, 7], [8, 0], [11, 12], [12, 7], [14, -99]],
    [[0, 0], [4, 7], [6, 9], [8, 12], [10, 7], [12, 0], [14, -99]],
  ];
  const pat = patterns[(c.song.seed + Math.floor(c.b / 2)) % patterns.length] as readonly (readonly [number, number])[];
  const split = c.chords.length > 1;
  for (const [step, iv] of pat) {
    const ch = split && step >= 8 ? (c.chords[1] as Chord) : chord;
    const r = split && step >= 8 ? bassRoot(ch) : root;
    let midi: number;
    if (iv === -99) midi = approach(r, nextRoot, rng, 28, 50);
    else midi = fitRange(r + (iv === 9 && (ch.quality === 'dom7' || ch.quality === 'min7') ? 10 : iv), 28, 50);
    if (step % 8 !== 0 && rng.chance(0.12)) continue; // the odd rest keeps it human
    e.push(ev('bass', step, midi, 2, step % 8 === 0 ? 0.95 : step % 4 === 0 ? 0.8 : 0.7));
  }
}

function bassWalking(c: BarCtx, upright: boolean): void {
  const { rng } = c;
  const chord = c.chords[0] as Chord;
  const root = bassRoot(chord, 31, 52);
  const nextRoot = bassRoot(c.next, 31, 52);
  const minor = chord.quality.startsWith('min') || chord.quality === 'm7b5';
  const third = minor ? 3 : 4;
  const fifth = chord.quality === 'm7b5' ? 6 : 7;
  const e = c.events;
  if (c.song.feel === 'bossa') {
    // Root on one, fifth on three, pickups on the "and" of two and four.
    const second = c.chords[1] ? bassRoot(c.chords[1] as Chord, 31, 52) : fitRange(root + fifth, 31, 52);
    e.push(ev('bass', 0, root, 5, 0.85), ev('bass', 6, root, 2, 0.5), ev('bass', 8, second, 5, 0.8), ev('bass', 14, approach(second, nextRoot, rng, 31, 52), 2, 0.5));
    return;
  }
  const lines: readonly (readonly number[])[] = [
    [0, third, fifth, -99],
    [0, 2, third, fifth],
    [0, fifth, 12, -99],
    [0, third, 2, -99],
    [0, 12, fifth, -99],
  ];
  const line = rng.pick(lines);
  const split = c.chords.length > 1;
  for (let i = 0; i < 4; i++) {
    const ch = split && i >= 2 ? (c.chords[1] as Chord) : chord;
    const r = split && i >= 2 ? bassRoot(ch, 31, 52) : root;
    const iv = line[i] as number;
    const midi = iv === -99 ? approach(r, nextRoot, rng, 31, 52) : fitRange(r + iv, 31, 52);
    e.push(ev('bass', i * 4, midi, upright ? 3.6 : 3.2, i === 0 ? 0.9 : 0.72));
    if (c.song.feel === 'swing' && i === 3 && rng.chance(0.3)) e.push(ev('bass', 14, fitRange(midi + 2, 31, 52), 1.5, 0.45));
  }
}

function bassSka(c: BarCtx): void {
  const { rng, song } = c;
  if (song.feel === 'reggae' && song.bassRiff.length) {
    bassFromRiff(c, song.bassRiff, 0.95);
    return;
  }
  const chord = c.chords[0] as Chord;
  const root = bassRoot(chord, 29, 50);
  const nextRoot = bassRoot(c.next, 29, 50);
  const minor = chord.quality.startsWith('min');
  const e = c.events;
  if (song.feel === 'ska') {
    // Ska walking: root, fifth, sixth / octave, approach — pushing eighths now and then.
    const walk = [root, fitRange(root + 7, 29, 50), fitRange(root + (minor ? 10 : 9), 29, 50), approach(root, nextRoot, rng, 29, 50)];
    walk.forEach((m, i) => e.push(ev('bass', i * 4, m, 3, i === 0 ? 0.95 : 0.78)));
    if (rng.chance(0.4)) e.push(ev('bass', 14, fitRange(root + 7, 29, 50), 1, 0.5));
    return;
  }
  // Rocksteady: slow, syncopated, roomy.
  e.push(ev('bass', 0, root, 3, 0.95), ev('bass', 3, root, 1, 0.6), ev('bass', 6, fitRange(root + 7, 29, 50), 2, 0.7));
  if (rng.chance(0.6)) e.push(ev('bass', 10, fitRange(root + (minor ? 10 : 9), 29, 50), 2, 0.6));
  e.push(ev('bass', 12, fitRange(root + 12, 29, 50), 2, 0.7));
  if (rng.chance(0.5)) e.push(ev('bass', 14, approach(root, nextRoot, rng, 29, 50), 2, 0.55));
}

function bassBeat(c: BarCtx): void {
  const { rng, song } = c;
  const chord = c.chords[0] as Chord;
  const root = bassRoot(chord, 28, 50);
  const nextRoot = bassRoot(c.next, 28, 50);
  const e = c.events;
  if (song.feel === 'rnb') {
    // R&B shuffle walking (12-bar): 1 3 5 6 up and down.
    const minor = chord.quality.startsWith('min');
    const line = c.b % 2 === 0 ? [0, minor ? 3 : 4, 7, 9] : [12, 10, 9, 7];
    line.forEach((iv, i) => e.push(ev('bass', i * 4, fitRange(root + iv, 28, 50), 3, i === 0 ? 0.95 : 0.78)));
    if (rng.chance(0.4)) e.push(ev('bass', 14, approach(root, nextRoot, rng, 28, 50), 2, 0.55));
    return;
  }
  if (song.bassRiff.length) {
    bassFromRiff(c, song.bassRiff, 0.92);
    return;
  }
  for (let s = 0; s < 16; s += 2) e.push(ev('bass', s, s === 14 ? approach(root, nextRoot, rng, 28, 50) : s === 6 || s === 12 ? fitRange(root + 7, 28, 50) : root, 2, s % 8 === 0 ? 0.95 : 0.75));
}

// ---- comp -----------------------------------------------------------------------------------

function compSoul(c: BarCtx): void {
  const { rng, song, sec } = c;
  const e = c.events;
  const inst = song.compInst;
  const vs = sec.voicings[c.b] as number[][];
  const split = c.chords.length > 1;
  const vel = 0.5 + 0.3 * c.energy;
  if (inst === 'rhodes') {
    const patterns: readonly (readonly [number, number][])[] = [
      [[0, 5], [6, 3], [10, 5]],
      [[0, 3], [4, 3], [8, 3], [12, 3]],
      [[0, 6], [7, 2], [10, 5]],
      [[2, 2], [6, 2], [10, 2], [14, 2]],
    ];
    const pat = song.feel === 'ballad' ? patterns[0] : patterns[(song.seed >>> 3) % patterns.length];
    for (const [step, dur] of pat as readonly (readonly [number, number])[]) {
      if (step !== 0 && rng.chance(0.15)) continue;
      const v = split && step >= 8 ? (vs[1] as number[]) : (vs[0] as number[]);
      for (const m of v) e.push(ev('rhodes', step, m, dur, vel * rng.range(0.85, 1)));
    }
    if (song.feel === 'ballad' && rng.chance(0.5)) {
      // A little arpeggiated turn at the top of the voicing.
      const v = vs[0] as number[];
      e.push(ev('rhodes', 12, (v[v.length - 1] as number) + 12, 2, vel * 0.5), ev('rhodes', 14, (v[v.length - 2] as number) + 12, 2, vel * 0.45));
    }
  } else {
    // Hammond: held chord, re-struck on the "and" of two.
    const v = vs[0] as number[];
    for (const m of v) e.push(ev('hammond', 0, m, split ? 8 : 16, vel * 0.8));
    if (split) for (const m of vs[1] as number[]) e.push(ev('hammond', 8, m, 8, vel * 0.8));
    else if (c.energy > 0.7) for (const m of v) e.push(ev('hammond', 6, m, 2, vel * 0.5));
  }
  // Guitar: clean chord chank on 2 and 4 (quieter), when the chorus or later verses want it.
  if (c.energy >= 0.7 && song.feel !== 'ballad') {
    const g = (vs[0] as number[]).slice(-3);
    for (const step of [4, 12]) for (const m of g) e.push(ev('guitarMute', step, m + 12 > 88 ? m : m + 12, 1, 0.5 * c.energy));
  }
  // Pad in the chorus / bridge.
  if (song.pad && (sec.kind === 'chorus' || sec.kind === 'bridge' || sec.kind === 'outro')) {
    const padInst: Inst = song.pad;
    const v = (vs[0] as number[]).map((m) => m + 12).filter((m) => m <= 91);
    for (const m of v) e.push(ev(padInst, 0, m, split ? 8 : 16, padInst === 'strings' ? 0.55 : 0.4));
    if (split) for (const m of (vs[1] as number[]).map((m) => m + 12).filter((m) => m <= 91)) e.push(ev(padInst, 8, m, 8, padInst === 'strings' ? 0.55 : 0.4));
  }
}

function compBeat(c: BarCtx): void {
  const { rng, song, sec } = c;
  const e = c.events;
  const vs = sec.voicings[c.b] as number[][];
  const split = c.chords.length > 1;
  const vel = 0.55 + 0.3 * c.energy;
  const vAt = (step: number): number[] => (split && step >= 8 ? (vs[1] as number[]) : (vs[0] as number[]));
  if (song.compInst === 'guitar') {
    // Driving eighth chops, muted off-beats; open on the backbeat.
    for (let s = 0; s < 16; s += 2) {
      const open = s % 4 === 0 || (s === 14 && rng.chance(0.5));
      for (const m of vAt(s).slice(-3)) e.push(ev(open ? 'guitar' : 'guitarMute', s, m, open ? 2 : 1, vel * (open ? 1 : 0.7)));
    }
  } else {
    // Hammond: chords on the backbeat with a held pad under the chorus.
    for (const s of [0, 4, 8, 12]) {
      if (s !== 0 && s !== 8 && rng.chance(0.2)) continue;
      for (const m of vAt(s)) e.push(ev('hammond', s, m, s % 8 === 0 ? 4 : 2, vel * (s % 8 === 0 ? 1 : 0.8)));
    }
    if (c.energy >= 0.85 || sec.kind === 'bridge') for (const m of vAt(0)) e.push(ev('hammond', 0, m - 12, 16, 0.35));
    // Guitar answers on the off-beats.
    if (c.energy >= 0.7) for (const s of [2, 6, 10, 14]) for (const m of vAt(s).slice(-2)) e.push(ev('guitarMute', s, m + 12 > 88 ? m : m + 12, 1, 0.45));
  }
  if (song.pad === 'hammond' && song.compInst === 'guitar' && (sec.kind === 'chorus' || sec.kind === 'bridge' || sec.kind === 'outro')) {
    for (const m of vAt(0)) e.push(ev('hammond', 0, m, split ? 8 : 16, 0.4));
    if (split) for (const m of vAt(8)) e.push(ev('hammond', 8, m, 8, 0.4));
  }
}

function compEasy(c: BarCtx): void {
  const { rng, song, sec } = c;
  const e = c.events;
  const vs = sec.voicings[c.b] as number[][];
  const split = c.chords.length > 1;
  const vel = 0.45 + 0.25 * c.energy;
  const inst: Inst = song.compInst === 'piano' ? 'piano' : 'rhodes';
  const vAt = (step: number): number[] => (split && step >= 8 ? (vs[1] as number[]) : (vs[0] as number[]));
  if (song.feel === 'bossa') {
    // Bossa comp: the syncopated pattern 1 . . & . . 4 / . & . . 3 . . &.
    const pat = c.b % 2 === 0 ? [0, 3, 6, 10, 12] : [2, 5, 8, 11, 14];
    for (const s of pat) for (const m of vAt(s)) e.push(ev(inst, s, m, 2, vel * (s === 0 ? 1 : 0.8)));
  } else if (song.feel === 'swing') {
    // Freddie Green-ish: light chords on 1 and 3, a push on the and of 4.
    for (const s of [0, 8]) for (const m of vAt(s)) e.push(ev(inst, s, m, 6, vel));
    if (rng.chance(0.6)) for (const m of vAt(14)) e.push(ev(inst, 14, m, 2, vel * 0.7));
  } else {
    // Lounge: long chords with a gentle arpeggio.
    for (const m of vAt(0)) e.push(ev(inst, 0, m, split ? 8 : 14, vel));
    if (split) for (const m of vAt(8)) e.push(ev(inst, 8, m, 8, vel));
    const v = vAt(0);
    const arp = [v[0], v[1], v[2], v[1]].filter((m): m is number => m !== undefined);
    arp.forEach((m, i) => e.push(ev(inst, 8 + i * 2, m + 12, 2, vel * 0.5)));
  }
  // Strings pad: every section but the intro of a bossa.
  if (song.pad === 'strings' && !(sec.kind === 'intro' && song.feel === 'bossa') && sec.kind !== 'solo') {
    const top = vAt(0).map((m) => m + 12).filter((m) => m <= 93);
    for (const m of top) e.push(ev('strings', 0, m, split ? 8 : 16, sec.kind === 'chorus' ? 0.6 : 0.45));
    if (split) for (const m of vAt(8).map((m) => m + 12).filter((m) => m <= 93)) e.push(ev('strings', 8, m, 8, sec.kind === 'chorus' ? 0.6 : 0.45));
  }
}

function compSka(c: BarCtx): void {
  const { rng, song, sec } = c;
  const e = c.events;
  const vs = sec.voicings[c.b] as number[][];
  const split = c.chords.length > 1;
  const vel = 0.55 + 0.3 * c.energy;
  const vAt = (step: number): number[] => (split && step >= 8 ? (vs[1] as number[]) : (vs[0] as number[]));
  const chop: Inst = song.compInst === 'guitar' ? 'guitarMute' : 'piano';
  if (song.feel === 'ska') {
    // The skank: chops on every off-beat (and-of), piano and guitar together.
    for (const s of [2, 6, 10, 14]) {
      for (const m of vAt(s).slice(-3)) e.push(ev(chop, s, m, 1, vel * (s === 6 || s === 14 ? 1 : 0.85)));
      for (const m of vAt(s).slice(-2)) e.push(ev(chop === 'piano' ? 'guitarMute' : 'piano', s, m + 12 > 88 ? m : m + 12, 1, vel * 0.55));
    }
  } else {
    // Rocksteady / reggae: chops on 2 and 4 (with the odd double-time "chk-chk").
    for (const s of [4, 12]) {
      for (const m of vAt(s).slice(-3)) e.push(ev(chop, s, m, 1, vel));
      if (rng.chance(song.feel === 'reggae' ? 0.5 : 0.25)) for (const m of vAt(s).slice(-3)) e.push(ev(chop, s + 2, m, 1, vel * 0.6));
    }
  }
  // Organ bubble: short off-beat sixteenths, a bar later in the verse, everywhere from the chorus.
  if (c.energy >= 0.7 || sec.kind === 'chorus') {
    const steps = song.feel === 'ska' ? [3, 7, 11, 15] : [2, 6, 10, 14];
    for (const s of steps) if (rng.chance(0.85)) for (const m of vAt(s).slice(0, 2)) e.push(ev('hammond', s, m - 12 < 40 ? m : m - 12, 1, 0.42));
  }
}

// ---- melody, stabs, lines -------------------------------------------------------------------

function melodyEvents(c: BarCtx): void {
  const { sec, song } = c;
  if (!sec.melody.length || !sec.melodyInst) return;
  const from = c.b * 16;
  const inst = melodyEventInst(sec.melodyInst);
  const quiet = sec.kind === 'verse' && (sec.nth === 0 || song.style === 'easy');
  if (quiet && sec.nth === 0 && song.style !== 'easy') return; // first verse: rhythm section only
  const vel = quiet ? 0.5 : sec.kind === 'intro' ? 0.75 : 0.9;
  for (const n of sec.melody) {
    if (n.step < from || n.step >= from + 16) continue;
    c.events.push(ev(inst, n.step - from, n.midi, n.dur, vel * n.acc));
  }
}

/** Brass / strings answering in the gaps of the chorus hook, and a hit on section downbeats. */
function stabs(c: BarCtx): void {
  const { sec, song, rng } = c;
  const e = c.events;
  if (sec.kind !== 'chorus' && sec.kind !== 'outro') return;
  if (song.hookInst === 'brass' && sec.nth === 0) return;
  const vs = sec.voicings[c.b] as number[][];
  const top = (vs[0] as number[]).slice(-3).map((m) => fitRange(m, 60, 81));
  const from = c.b * 16;
  // Where the hook rests for at least 4 steps, put a two-note stab.
  const covered = new Array<boolean>(16).fill(false);
  for (const n of sec.melody) for (let s = Math.max(from, n.step); s < Math.min(from + 16, n.step + n.dur); s++) covered[s - from] = true;
  let run = 0;
  let placed = 0;
  for (let s = 0; s < 16; s++) {
    run = covered[s] ? 0 : run + 1;
    if (run === 3 && placed < 2 && rng.chance(0.8)) {
      const step = s - 1;
      for (const m of top) e.push(ev('brass', step, m, 2, 0.7 * c.energy));
      placed++;
      run = 0;
    }
  }
  if (c.firstBar && song.style !== 'easy') for (const m of top) e.push(ev('brass', 0, m, 3, 0.8));
}

/** Apply the outro fade (or silence after the cold hit). */
function finish(c: BarCtx): void {
  const { sec, song } = c;
  if (sec.kind !== 'outro') return;
  if (song.ending === 'cold') {
    if (c.lastBar) {
      const keep = c.events.filter((x) => x.step === 0);
      c.events.length = 0;
      // The final chord: everyone on the downbeat, then nothing.
      const vs = sec.voicings[c.b] as number[][];
      for (const m of vs[0] as number[]) keep.push(ev(song.compInst, 0, m, 8, 0.9));
      keep.push(ev('bass', 0, bassRoot(c.chords[0] as Chord), 8, 1));
      c.events.push(...keep.filter((x) => x.inst !== 'hat' && x.inst !== 'ride'));
    }
    return;
  }
  const total = sec.bars * 16;
  for (const x of c.events) {
    const g = 1 - (c.b * 16 + x.step) / total;
    x.vel = Math.max(0.05, x.vel * Math.pow(g, 1.4));
  }
}

/** One bar of a song as note events. Pure and deterministic. */
export function songBar(song: Song, bar: number): NoteEvent[] {
  const sec = sectionAt(song, bar);
  const b = bar - sec.start;
  const chords = sec.chords[b] as Chord[];
  const nextSec = bar + 1 < song.bars ? sectionAt(song, bar + 1) : null;
  const next = nextSec ? ((nextSec.chords[bar + 1 - nextSec.start] as Chord[])[0] as Chord) : (chords[0] as Chord);
  const c: BarCtx = {
    song,
    sec,
    b,
    rng: new Rng((song.seed ^ Math.imul(bar + 1, 0x9e3779b1)) >>> 0),
    chords,
    next,
    root: bassRoot(chords[0] as Chord),
    events: [],
    energy: energyOf(sec),
    fill4: b % 4 === 3 && b !== sec.bars - 1,
    fill8: b === sec.bars - 1 && sec.kind !== 'outro',
    lastBar: b === sec.bars - 1,
    firstBar: b === 0,
  };
  switch (song.style) {
    case 'soul':
      drumsSoul(c);
      bassSoul(c);
      compSoul(c);
      break;
    case 'beat':
      drumsBeat(c);
      bassBeat(c);
      compBeat(c);
      break;
    case 'easy':
      drumsEasy(c);
      bassWalking(c, true);
      compEasy(c);
      break;
    default:
      drumsSka(c);
      bassSka(c);
      compSka(c);
  }
  // Intro of the first bar: a lighter start (no bass for a bar on half the songs).
  if (sec.kind === 'intro' && b === 0 && song.seed % 2 === 0) {
    const keep = c.events.filter((x) => x.inst !== 'bass');
    c.events.length = 0;
    c.events.push(...keep);
  }
  melodyEvents(c);
  stabs(c);
  finish(c);
  // Range safety for every pitched part.
  for (const x of c.events) {
    if (!PITCHED.includes(x.inst)) continue;
    x.midi = x.inst === 'bass' ? fitRange(x.midi, 26, 55) : fitRange(x.midi, 40, 96);
    x.step = Math.max(0, Math.min(15, Math.round(x.step)));
  }
  return c.events;
}

/** Seconds per sixteenth step at a tempo. */
export function stepSeconds(bpm: number): number {
  return 60 / bpm / 4;
}

/** Song length in seconds. */
export function songSeconds(song: Song): number {
  return song.bars * 16 * stepSeconds(song.bpm);
}

/** Offset (in steps) added at playback to swing the off-beats. */
export function swingOffset(step: number, swing: number): number {
  if (step % 4 === 2) return swing * 0.667;
  if (step % 2 === 1) return swing * 0.333;
  return 0;
}

/** Does the melody note at this bar / step sit on a chord tone (analysis helper for tests)? */
export function melodyOnChordTone(song: Song, bar: number, e: NoteEvent): boolean {
  const sec = sectionAt(song, bar);
  const chord = chordAtStep(sec.chords[bar - sec.start] as Chord[], e.step);
  return isChordTone(chord, e.midi);
}
